import { Card, Grid, Icon, IconButton, InputLabel, Tooltip } from "@mui/material";
import MDInput from "components/MDInput";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDSelect from "components/MDSelect";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import { useUser } from "context/UserContext";
import DataTable from "examples/Tables/DataTable";
import DefaultCell from "layouts/dashboards/sales/components/DefaultCell";
import { useCallback, useEffect, useRef, useState } from "react";
import { verifyVariable } from "utils/Functions";
import { post } from "utils/WebService";
import LawyersCreateEdit from "./LawyersCreateEdit";
import { ESCURO } from "assets/coresTema";

export default function LawyersConsult() {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;

    const tableState = useRef({
        pageSize: 10,
        pageIndex: 0,
        sorting: [],
        filtered: [],
        tipoPessoa: null
    });

    const { processoPessoaTipo } = useUser();

    const [data, setData] = useState([]);
    const [pages, setPages] = useState(0);
    const [loadingTable, setLoadingTable] = useState(true);
    const [isCreateEdit, setIsCreateEdit] = useState(false);
    const [selectedId, setSelectedId] = useState(null);

    const [filters, setFilters] = useState({
        nome: "",
        tipoPessoa: null
    });

    const [refresh, setRefresh] = useState(false);
    const [recordsTotal, setRecordsTotal] = useState(0);

    useEffect(() => {
        fetchData({ ...tableState });
    }, []);

    const fetchData = useCallback(({ pageSize, pageIndex, sorting, filtered, tipoPessoa }) => {
        setLoadingTable(true);

        const updatedTableState = {
            pageSize: pageSize ? pageSize : tableState.current.pageSize,
            pageIndex: (pageIndex == 0 || pageIndex) ? pageIndex : tableState.current.pageIndex,
            sorting: sorting ? sorting : tableState.current.sorting,
            filtered: filtered ? filtered : tableState.current.filtered,
            tipoPessoa: tipoPessoa ? tipoPessoa : tableState.current.tipoPessoa,
        };

        tableState.current = updatedTableState;
        post("/lawyer/list", {
            page: updatedTableState.pageIndex,
            pageSize: updatedTableState.pageSize,
            sorted: updatedTableState.sorting,
            filtered: updatedTableState.filtered,
            tipoPessoa: updatedTableState.tipoPessoa,
        }).then(({ data, pages, recordsTotal }) => {
            setPages(pages);
            setData(data);
            setRecordsTotal(recordsTotal);
            setLoadingTable(false);
        }).catch(() => setLoadingTable(false));

        setRefresh(false);

    }, [refresh]);

    const backFunction = () => {
        setIsCreateEdit(false);
        setSelectedId(null);
    }

    const handleRefresh = () => {
        setRefresh(true);
    };

    const userColumns = [
        {
            header: "nome",
            accessorKey: "nome",
            cell: info => <DefaultCell>{info.getValue() || '-'}</DefaultCell>,
            isSorted: true
        },
        {
            header: "logradouro",
            accessorKey: "logradouro",
            cell: info => <DefaultCell>{info.getValue() || '-'}</DefaultCell>,
            isSorted: true
        },
        {
            header: "Nº/Bairro",
            accessorKey: "numeroEndereco",
            cell: info => {
                const numero = info.getValue();
                const bairro = info.row.original.bairro;
                return <DefaultCell>{(numero && bairro) ? `${numero}/${bairro}` : '-'}</DefaultCell>;
            },
            isSorted: true
        },
        {
            header: "Cidade/Estado",
            accessorKey: "cidade",
            cell: info => {
                const cidade = info.getValue();
                const estado = info.row.original.estado;
                return <DefaultCell>{(cidade && estado) ? `${cidade}/${estado}` : '-'}</DefaultCell>;
            },
            isSorted: true
        },
        {
            header: "Telefone",
            accessorKey: "telefone",
            cell: info => <DefaultCell>{info.getValue() || '-'}</DefaultCell>,
            isSorted: true
        },
        {
            header: "Inscrição",
            accessorKey: "inscricao",
            cell: info => <DefaultCell>{info.getValue() || '-'}</DefaultCell>,
            isSorted: true
        },
        {
            header: "Seccional",
            accessorKey: "seccional",
            cell: info => <DefaultCell>{info.getValue() || '-'}</DefaultCell>,
            isSorted: true
        },
        {
            header: "Subsessão",
            accessorKey: "subsessao",
            cell: info => <DefaultCell>{info.getValue() || '-'}</DefaultCell>,
            isSorted: true
        },
        {
            header: "ações",
            isSorted: false,
            cell: info => (
                <>
                    <Tooltip title="Editar Advogado">
                        <IconButton
                            onClick={() => {
                                setSelectedId(info.row.original.id);
                                setIsCreateEdit(true);
                            }}>
                            <MDTypography variant="h4">
                                <Icon color="inherit" sx={{ marginRight: 1 }}>edit</Icon>
                            </MDTypography>
                        </IconButton>
                    </Tooltip>
                </>
            ),
            width: "10%"
        },
    ];

    const handleApplyFilter = () => {
        let filtered = [];
        Object.keys(filters).forEach(filterKey => {
            if (verifyVariable(filters[filterKey]) && filterKey != "tipoPessoa") {
                filtered.push({ id: filterKey, value: filters[filterKey] });
            }
        })
        fetchData({ ...tableState, filtered, tipoPessoa: filters.tipoPessoa?.id });
    };

    const handleCleanFilter = () => {
        setFilters({
            nome: "",
            tipoPessoa: null
        });
        const updatedTableState = {
            pageSize: 10,
            pageIndex: 0,
            sorting: [],
            filtered: [],
            tipoPessoa: null
        };

        tableState.current = updatedTableState;
        fetchData(updatedTableState);
    };

    return (
        <Grid container display="flex" justifyContent="center">
            {isCreateEdit &&
                <Grid item xs={12} md={12}>
                    <LawyersCreateEdit
                        lawyerId={selectedId}
                        closeCreateEdit={backFunction}
                        refreshTable={handleRefresh} />
                </Grid>
            }
            <Grid item xs={12} md={12} sx={{ display: isCreateEdit ? "none" : "" }}>
                <Card
                    variant="outlined"
                    sx={{
                        px: 2,
                        py: 1.5,
                        mb: 2,
                        background: darkMode ? ESCURO.cartao : "#fafafa",
                        borderColor: darkMode ? ESCURO.divisoria : "#ddd",
                    }}
                >
                    <Grid container spacing={1}>
                            <Grid item xs={3} sm={4}>
                                <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Nome</InputLabel>
                                <MDInput
                                    size="small"
                                    placeholder="Informe o nome..."
                                    value={filters.nome}
                                    onChange={({ target }) => setFilters({ ...filters, nome: target.value })}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            handleApplyFilter();
                                        }
                                    }}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Tipo Pessoa</InputLabel>
                                <MDSelect
                                    isMulti={false}
                                    getOptionValue={option => option.code}
                                    getOptionLabel={option => option.name}
                                    name="tipo-multi-select"
                                    placeholder="Todos"
                                    value={filters.tipoPessoa || null}
                                    options={processoPessoaTipo}
                                    onChange={selectedOptions => setFilters({ ...filters, tipoPessoa: selectedOptions })}
                                />
                            </Grid>
                            <Grid item xs={12} display="flex" alignItems="center" justifyContent="space-between" pt={1}>
                                <MDBox>
                                    <MDButton
                                        size="medium"
                                        variant="contained"
                                        color="info"
                                        circular={false}
                                        iconOnly={false}
                                        onClick={() => {
                                            setSelectedId(null);
                                            setIsCreateEdit(true);
                                        }}>
                                        Adicionar Advogado
                                    </MDButton>
                                </MDBox>
                                <MDBox>
                                    <MDButton
                                        size="medium"
                                        sx={{ marginRight: 2 }}
                                        variant="contained"
                                        color="info"
                                        circular={false}
                                        iconOnly={false}
                                        onClick={handleCleanFilter}>
                                        Limpar filtro
                                    </MDButton>
                                    <MDButton size="medium" variant="contained" color="info" circular={false} iconOnly={false} onClick={handleApplyFilter}>
                                        Aplicar filtro
                                    </MDButton>
                                </MDBox>
                            </Grid>
                    </Grid>
                </Card>
                <DataTable
                    loading={loadingTable}
                    columnData={userColumns}
                    data={data}
                    fetchData={fetchData}
                    totalPages={pages}
                    totalRecords={recordsTotal}
                    tableState={tableState} />
            </Grid>
        </Grid >
    );
}
